import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useImageOptimization } from '../utils/imageDisplayOptimizer';
import { fallbackUploadsSrc, getImageUrl } from '../utils/imageUrl';
import { getPropertyTypeLabel } from '../utils/propertyTypeUtils';
import { contractApi, Contract } from '../services/contractApi';
import { propertyApi } from '../services/propertyApi';
import { orderApi } from '../services/orderApi';
import type { ContractSummary } from '../services/contractApi';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { usePropertyContext } from '../contexts/PropertyContext';
import { useNotifications } from '../contexts/NotificationContext';
import { NotificationStatus } from '../services/notificationApi';
import { API_BASE_URL } from '../config/api';
import { AMENITY_ICON_MAP, AMENITY_NAME_MAP } from './Asset/constants';
import AddContractModal, { ContractFormPreset, AddContractModalMode } from './AddContractModal';
import RoomPayQRModal from './RoomPayQRModal';
import { filterPropertiesBySearchQueryAndStatusAndList } from '../utils/propertySearch';
import { getPropertyDisplayHeading } from '../utils/propertyAddress';
import { createSavedPropertyList, fetchSavedPropertyLists, persistSavedPropertyLists, type SavedPropertyList } from '../utils/propertyLists';
import { ProfileType } from '../utils/profileType';
import { requestDashboardRefresh } from '../utils/dashboardRefresh';
import {
  CONTRACT_REQUEST_EVENT,
  contractPresetFromRequestMetadata,
  getContractRequestMetadata,
} from '../utils/contractRequestNotification';

const transformAmenityData = (amenities: any): Array<{ icon: string; label: string; count?: number }> => {
  if (!amenities) return [];
  if (Array.isArray(amenities)) {
    return amenities.map((item: any) => ({
      icon: typeof item.icon === 'string' ? item.icon : 'fas fa-box',
      label: item.label || item.name || item.type || String(item),
      count: item.count
    }));
  }

  const rawAmenities = typeof amenities === 'object' && 'amenities' in amenities ? amenities.amenities : amenities;
  if (!rawAmenities || typeof rawAmenities !== 'object') return [];

  return Object.entries(rawAmenities).reduce((acc, [key, value]) => {
    const count = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
    const normalizedKey = key.toLowerCase();
    const icon = AMENITY_ICON_MAP[normalizedKey] || AMENITY_ICON_MAP[key] || 'fas fa-box';
    const label = AMENITY_NAME_MAP[normalizedKey] || AMENITY_NAME_MAP[key] || key;
    acc.push({ icon, label, count: count > 1 ? count : undefined });
    return acc;
  }, [] as Array<{ icon: string; label: string; count?: number }>);
};

interface PropertyOwner {
  id: number;
  full_name?: string;
  username?: string;
  avatar?: string;
  is_verified?: boolean;
  email?: string;
  subscription_level?: string;  // ✅ Added
  subscription_end_date?: string | null;  // ✅ Added
  account_type?: string;  // ✅ Added
}

interface Property {
  id: number;
  title: string;
  type: string;
  price: number;
  area: number;
  bedrooms: number;
  bathrooms: number;
  status: string;
  main_image: string;
  gallery_images?: string[];
  video_url?: string;
  created_at: string;
  room_number?: string;  // 💡 Mã số phòng/căn hộ
  pay_room_code?: string;
  pay_url?: string | null;
  address?: any;
  // Address fields - prefer these direct fields
  full_address?: string;
  street?: string;
  ward_name?: string;
  ward_code?: string;
  province_name?: string;
  province_code?: string;
  // Owner information
  owner_id?: number;
  owner?: PropertyOwner;
  total_views?: number;
  total_favorites?: number;
  total_contacts?: number;
  unique_viewers?: number;
  // Meilisearch ranking score
  _rankingScore?: number;
  // For tenant: contract info
  contract?: Contract & { totalPaid?: number; payments?: any[] };
  contractStatus?: string; // 'Đang Đặt Thuê' | 'Đang Thuê'
  amenities?: any;
}

interface PropertyListingsProps {
  accountType: ProfileType;
  ownerId?: number; // Optional: filter properties by specific owner ID (for public profiles)
  onPropertiesLoaded?: (hasProperties: boolean) => void; // Callback để thông báo có properties hay không
  isPublicProfile?: boolean; // Flag để xác định có phải đang xem profile công khai không
  searchQuery?: string;
  hideOwnerToolbar?: boolean;
  compactNative?: boolean;
  ownerActionsRef?: React.MutableRefObject<{
    openCreateContract: () => void;
    openCreateList: () => void;
    openListActions: () => void;
  } | null>;
}

// Property Card Component with Image Slider
const PropertyCard: React.FC<{ property: Property; accountType?: ProfileType; isPublicProfile?: boolean; shouldShowUpgradeBlur?: boolean; compactNative?: boolean; onViewDetails?: (property: Property) => void; onShowRoomQR?: (property: Property) => void; onDelete?: (property: Property) => void }> = ({ property, accountType = 'landlord', isPublicProfile = false, shouldShowUpgradeBlur = false, compactNative = false, onViewDetails, onShowRoomQR, onDelete }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Sử dụng Image Optimizer với chiều sâu và độ sắc nét (BỎ HOVER)
  const imageOptimizer = useImageOptimization({
    mode: 'cover',
    enableDepth: true,
    enableSharpness: true,
    hoverScale: 1.0, // Bỏ hover scale
  });
  
  const status = getStatusBadge(property.status, t);
  const hasContract = hasUnexpiredRentalContract(property.contract);

  useEffect(() => {
    if (!isActionsMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setIsActionsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isActionsMenuOpen]);
  
  // Check KYC verification status of owner
  const ownerIsKYCVerified = property.owner?.is_verified ?? false;
  const isOwnProperty = user && property.owner_id ? parseInt(user.id) === property.owner_id : false;
  // Only show blur in PropertyListings when user views their own unverified products
  const shouldShowBlurred = !ownerIsKYCVerified && isOwnProperty && !isPublicProfile;
  
  // ✅ Use prop from parent component which calculates blur based on subscription + index
  // shouldShowUpgradeBlur is passed from parent PropertyListings component
  // It checks: isPublicProfile && !isSubscriptionActive && propertyIndex >= 5
  
  const handleEndContract = (e: React.MouseEvent) => {
    e.preventDefault();
    showAlert({
      type: 'warning',
      title: t('propertyListings.confirmEndContract'),
      message: t('propertyListings.confirmEndContractMessage'),
      duration: 0 // Don't auto-hide for confirmation
    });
    // TODO: Implement confirmation dialog and end contract functionality
  };
  
  // Safely parse address - handle both string and object formats
  let addressDisplay = '';
  try {
    if (property.full_address) {
      // Prefer full_address if available
      addressDisplay = property.full_address;
    } else if (property.street && property.ward_name) {
      // Build from components
      addressDisplay = `${property.street} - ${property.ward_name}${property.province_name ? ` - ${property.province_name}` : ''}`;
    } else if (typeof property.address === 'string') {
      // If address is a string, use it directly (don't try to parse as JSON)
      addressDisplay = property.address;
    } else if (property.address && typeof property.address === 'object') {
      // If address is an object, try to extract meaningful parts
      if (property.address.full_address) {
        addressDisplay = property.address.full_address;
      } else if (property.address.street && property.address.ward_name) {
        addressDisplay = `${property.address.street} - ${property.address.ward_name}${property.address.province_name ? ` - ${property.address.province_name}` : ''}`;
      } else if (property.address.street || property.address.ward || property.address.province) {
        // Try to build from address object components
        const parts = [
          property.address.street,
          property.address.ward_name || property.address.ward,
          property.address.province_name || property.address.province
        ].filter(Boolean);
        addressDisplay = parts.join(' - ');
      } else {
        // Last resort: try JSON stringify if it's an object
        addressDisplay = JSON.stringify(property.address);
      }
    } else {
      addressDisplay = t('propertyListings.noAddress');
    }
  } catch (error) {
    console.error(`Error parsing address for property ${property.id}:`, error);
    addressDisplay = property.full_address || property.address || t('propertyListings.noAddress');
  }
  
  // Kết hợp và xử lý tất cả ảnh
  const images = imageOptimizer.combineImages(property.main_image, property.gallery_images).map((src) => getImageUrl(src) || src);
  
  console.log(`Property ${property.id} "${property.title}" - Total images: ${images.length}`);

  // Removed unused handlePrevImage and handleNextImage handlers

  const handleDotClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  // Tính toán container dimensions
  const containerDimensions = { width: 112, height: 280 };
  
  // Image display style (BỎ HOVER EFFECT)
  const imageStyle = imageOptimizer.getStyle(containerDimensions) as React.CSSProperties;

  if (compactNative) {
    return (
      <div className={`rounded-xl border border-black/10 bg-white px-3 py-2.5 ${isActionsMenuOpen ? 'relative z-50 overflow-visible' : 'overflow-hidden'}`}>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-sm font-semibold text-black">
                {property.room_number ? `Căn ${property.room_number}` : 'Chưa có số căn'}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.class}`}>
                {status.text}
              </span>
            </div>
            <p className="line-clamp-2 text-xs text-black/60">
              <i className="fas fa-map-marker-alt mr-1 text-[#C4A35A]" />
              {addressDisplay}
            </p>
          </div>
          {!isPublicProfile && (
            <div className="relative shrink-0" ref={actionsMenuRef}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsActionsMenuOpen((isOpen) => !isOpen);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-black/5 text-black/60"
                aria-label="Thao tác sản phẩm"
              >
                <i className="fas fa-ellipsis-v text-sm" />
              </button>
              {isActionsMenuOpen && (
                <div
                  className="absolute right-0 z-50 mt-1 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link
                    to={`/edit-product/${property.id}`}
                    state={{
                      rent_payment_day: (property as any).rent_payment_day ?? (property as any).rentPaymentDay,
                      property,
                    }}
                    onClick={() => setIsActionsMenuOpen(false)}
                    className="block px-3 py-2 text-sm text-black/80 hover:bg-[#C4A35A]/10"
                  >
                    {t('propertyListings.edit')}
                  </Link>
                  {onViewDetails && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsActionsMenuOpen(false);
                        onViewDetails(property);
                      }}
                      className="block w-full px-3 py-2 text-left text-sm text-black/80 hover:bg-[#C4A35A]/10"
                    >
                      {t('propertyListings.viewDetails')}
                    </button>
                  )}
                  {onShowRoomQR && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsActionsMenuOpen(false);
                        onShowRoomQR(property);
                      }}
                      className="block w-full px-3 py-2 text-left text-sm text-black/80 hover:bg-[#C4A35A]/10"
                    >
                      QR phòng
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsActionsMenuOpen(false);
                        onDelete(property);
                      }}
                      className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                    >
                      {t('propertyListings.delete')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-black/5 hover:border-[#C4A35A]/20 transition-all duration-300 group ${isActionsMenuOpen ? 'overflow-visible relative z-50' : 'overflow-hidden'} ${shouldShowBlurred || shouldShowUpgradeBlur ? 'relative' : ''}`}>
      {/* KYC Warning Overlay - Show when owner is not verified and user is not owner */}
      {shouldShowBlurred && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-xl z-20 flex flex-col items-center justify-center pointer-events-auto">
          <div className="text-center">
            <i className="fas fa-hourglass-end text-white text-3xl mb-2"></i>
            <p className="text-white font-semibold text-sm">Chờ KYC</p>
            <p className="text-white/70 text-xs mt-1">Hoàn thành KYC để hiển thị</p>
          </div>
        </div>
      )}
      
      {/* Upgrade Limit Blur Overlay */}
      {shouldShowUpgradeBlur && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-xl z-20 flex flex-col items-center justify-center pointer-events-auto">
          <div className="text-center">
            <i className="fas fa-star text-amber-400 text-3xl mb-2"></i>
            <p className="text-white font-semibold text-sm">Chờ Nâng Hạng</p>
            <p className="text-white/70 text-xs mt-1">Nâng hạng để hiển thị</p>
          </div>
        </div>
      )}
      {/* Mobile: Vertical product card | Desktop: Horizontal list row */}
      <div className={`flex flex-col lg:flex-row lg:h-[200px] ${isActionsMenuOpen ? 'overflow-visible' : 'overflow-hidden'}`}>
        <div 
          className={`relative w-full lg:w-2/5 flex-shrink-0 overflow-hidden bg-black shadow-md aspect-video lg:aspect-auto ${isActionsMenuOpen ? 'z-0' : ''} ${(shouldShowBlurred || shouldShowUpgradeBlur) ? 'blur-md' : ''}`}
          style={{ 
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.08)',
            borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px'  
          }}
        >
          <img
            src={images[currentImageIndex] || '/img/Product/Product (1).jpeg'}
            alt={getPropertyDisplayHeading(property, { alreadyShowsAddress: true })}
            style={imageStyle}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              const original = images[currentImageIndex];
              const next = fallbackUploadsSrc(img.src, original);
              if (next) {
                img.src = next;
                return;
              }
              img.src = '/img/Product/Product (1).jpeg';
            }}
          />
          
          {/* Property Type Badge + Room Number + Ranking Score */}
          <div className="absolute top-2 left-2 z-10 flex gap-1.5 items-center flex-wrap">
            {/* Room number badge - show only on personal profile (owner view) */}
            {(!isPublicProfile && accountType === 'landlord' && property.room_number) && (
              <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-black/60 text-white shadow-md">
                Căn {property.room_number}
              </span>
            )}
            <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-[#C4A35A] text-white shadow-md">
              {getPropertyTypeLabel(property.type || 'apartment')}
            </span>
            {/* ✅ Display Meilisearch Ranking Score */}
            {property._rankingScore !== undefined && property._rankingScore > 0 && (
              <span 
                className="px-2.5 py-1 rounded-md text-xs font-semibold text-white shadow-md flex items-center gap-1"
                style={{
                  backgroundColor: `hsl(${Math.min(property._rankingScore * 120, 120)}, 70%, 45%)`,  // Green → Yellow gradient
                  opacity: 0.9
                }}
                title={`Meilisearch Ranking: ${(property._rankingScore * 100).toFixed(1)}%`}
              >
                <i className="fas fa-fire text-xs"></i>
                {Math.round(property._rankingScore * 100)}%
              </span>
            )}
          </div>

          {/* Status Badge - moved to top-right of image */}
          <div className="absolute top-2 right-2 z-20">
            {accountType === 'tenant' && property.contractStatus ? (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                property.contractStatus === 'Đang Đặt Thuê'
                  ? 'bg-blue-100 text-blue-800 border border-blue-200'
                  : 'bg-[#C4A35A]/10 text-[#C4A35A] border border-[#C4A35A]/20'
              }`}>
                {property.contractStatus === 'Đang Đặt Thuê' ? t('propertyListings.contractStatus.booking') :
                 property.contractStatus === 'Đang Thuê' ? t('propertyListings.contractStatus.renting') :
                 property.contractStatus === 'Đã Hoàn Thành' ? t('propertyListings.contractStatus.completed') :
                 property.contractStatus === 'Hết Hạn' ? t('propertyListings.contractStatus.expired') :
                 property.contractStatus}
              </span>
            ) : (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${status.class}`}>
                {status.text}
              </span>
            )}
          </div>

          {/* Tracking Stats Overlay */}
          <div className="absolute bottom-2 left-2 flex items-center gap-2 z-10">
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm">
              <i className="far fa-eye text-white text-xs"></i>
              <span className="text-white text-xs font-medium">{property.total_views || 0}</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm">
              <i className="far fa-heart text-white text-xs"></i>
              <span className="text-white text-xs font-medium">{property.total_favorites || 0}</span>
            </div>
          </div>

          {/* Image Dots - Visible with background */}
          {images.length > 1 && (
            <div className="absolute bottom-2 right-2 flex gap-1.5 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm z-10">
              {images.map((_: string, index: number) => (
                <button
                  key={index}
                  onClick={(e) => handleDotClick(e, index)}
                  className={`transition-all duration-300 ${
                    index === currentImageIndex 
                      ? 'w-5 h-1.5 bg-white shadow-sm' 
                      : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
                  } rounded-full`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content - Mobile: full width, responsive padding - Constrained height on desktop */}
        <div className={`flex-1 min-w-0 flex flex-col ${isActionsMenuOpen ? 'overflow-visible relative z-10' : 'overflow-hidden lg:overflow-y-auto'} p-3 lg:p-4 !p-3 lg:!p-4 ${(shouldShowBlurred || shouldShowUpgradeBlur) ? 'blur-sm opacity-60' : ''}`}>
          {/* Debug Log */}
          {(() => {
            if (property.room_number) {
              console.log(`🏘️ [PropertyListings] Rendering property ${property.id}: room_number = "${property.room_number}"`);
            }
            return null;
          })()}
          
          {/* Address & Room Number - Mobile: stack, Desktop: flex */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2 sm:mb-3 !gap-2 !mb-2 sm:!mb-3">
            <p className="text-xs sm:text-sm text-black flex items-center flex-1 min-w-0 !m-0 !p-0">
              <i className="fas fa-map-marker-alt text-[#C4A35A] mr-1.5 text-xs flex-shrink-0"></i>
              <span className="line-clamp-1">{addressDisplay}</span>
            </p>
            {/* Room number is shown as a top-left badge for owner view; inline display removed */}
          </div>

          {/* Details - Mobile: 2-3 cols, compact text */}
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-black mb-2 sm:mb-3 flex-wrap !gap-2 sm:!gap-4 !mb-2 sm:!mb-3">
            <span className="flex items-center gap-1">
              <i className="fas fa-expand-arrows-alt text-[#C4A35A] text-xs"></i>
              <span className="line-clamp-1">{property.area}m²</span>
            </span>
            {property.bedrooms > 0 && (
              <span className="flex items-center gap-1">
                <i className="fas fa-bed text-[#C4A35A] text-xs"></i>
                <span className="line-clamp-1">{property.bedrooms}</span>
              </span>
            )}
            {property.bathrooms > 0 && (
              <span className="flex items-center gap-1">
                <i className="fas fa-bath text-[#C4A35A] text-xs"></i>
                <span className="line-clamp-1">{property.bathrooms}</span>
              </span>
            )}
          </div>

          {/* Price & Actions */}
          <div className="flex items-center justify-between mt-auto !mt-auto">
            <div className="!m-0 !p-0">
              <div className="text-xl font-bold text-black !m-0 !p-0">
                {formatCurrency(property.price)}<span className="text-sm font-normal text-gray-600">{t('propertyListings.pricePerMonth')}</span>
              </div>
              {accountType === 'tenant' && property.contract?.totalPaid && property.contract.totalPaid > 0 && (
                <div className="text-xs text-blue-700 font-medium mt-1">
                  {t('propertyListings.paidAmount', { amount: formatCurrency(property.contract.totalPaid) })}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {accountType === 'tenant' ? (
                <>
                  <Link
                    to={`/product/${property.id}`}
                    className="w-9 h-9 rounded-lg bg-black/5 hover:bg-[#C4A35A] hover:text-white text-black/70 transition-all duration-200 flex items-center justify-center"
                    title={t('propertyListings.viewDetails')}
                  >
                    <i className="fas fa-eye text-sm"></i>
                  </Link>
                  {property.contract && (
                    <Link
                      to={`/contracts/${property.contract.id}`}
                      className="w-9 h-9 rounded-lg bg-black/5 hover:bg-black hover:text-white text-black/70 transition-all duration-200 flex items-center justify-center"
                      title={t('propertyListings.viewContract')}
                    >
                      <i className="fas fa-file-contract text-sm"></i>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {!isPublicProfile && (
                    <>
                      {hasContract ? (
                        <Link
                          to={`/contracts/${property.contract?.id ?? property.id}`}
                          className="rounded-lg bg-black/5 hover:bg-[#C4A35A] hover:text-white text-black/70 transition-all duration-200 flex items-center justify-center px-3 py-2 text-xs font-semibold whitespace-nowrap"
                          title={t('propertyListings.viewContract')}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Xem hợp đồng
                        </Link>
                      ) : null}
                      <div className="relative" ref={actionsMenuRef}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsActionsMenuOpen((isOpen) => !isOpen);
                          }}
                          className="w-9 h-9 rounded-lg bg-black/5 hover:bg-[#C4A35A] hover:text-white text-black/70 transition-all duration-200 flex items-center justify-center"
                          title="Thao tác sản phẩm"
                          aria-label="Thao tác sản phẩm"
                          aria-expanded={isActionsMenuOpen}
                        >
                          <i className="fas fa-ellipsis-v text-sm"></i>
                        </button>
                        {isActionsMenuOpen && (
                          <div
                            className="absolute right-0 z-50 min-w-[170px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg top-full mt-1 lg:top-auto lg:bottom-11 lg:mt-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Link
                              to={`/edit-product/${property.id}`}
                              state={{
                                rent_payment_day: (property as any).rent_payment_day ?? (property as any).rentPaymentDay,
                                property,
                              }}
                              onClick={() => setIsActionsMenuOpen(false)}
                              className="block px-3 py-2 text-sm text-black/80 hover:bg-[#C4A35A]/10"
                            >
                              {t('propertyListings.edit')}
                            </Link>
                            {onViewDetails ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setIsActionsMenuOpen(false);
                                  onViewDetails(property);
                                }}
                                className="block w-full px-3 py-2 text-left text-sm text-black/80 hover:bg-[#C4A35A]/10"
                              >
                                {t('propertyListings.viewDetails')}
                              </button>
                            ) : (
                              <Link
                                to={`/detail/${property.id}`}
                                onClick={() => setIsActionsMenuOpen(false)}
                                className="block px-3 py-2 text-sm text-black/80 hover:bg-[#C4A35A]/10"
                              >
                                {t('propertyListings.viewDetails')}
                              </Link>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setIsActionsMenuOpen(false);
                                if (onShowRoomQR) onShowRoomQR(property);
                              }}
                              className="block w-full px-3 py-2 text-left text-sm text-black/80 hover:bg-[#C4A35A]/10"
                            >
                              QR phòng
                            </button>
                            {onDelete && (
                              <button
                                type="button"
                                onClick={() => {
                                  setIsActionsMenuOpen(false);
                                  onDelete(property);
                                }}
                                className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                              >
                                {t('propertyListings.delete')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {property.contract?.workflow_current_step === 'completed' && (
                        <button
                          className="w-9 h-9 rounded-lg bg-black/5 hover:bg-black hover:text-white text-black/70 transition-all duration-200 flex items-center justify-center"
                          title={t('propertyListings.endContract')}
                          onClick={handleEndContract}
                        >
                          <i className="fas fa-file-contract text-sm"></i>
                        </button>
                      )}
                    </>
                  )}
                  {isPublicProfile && (
                    <Link
                      to={`/detail/${property.id}`}
                      className="w-9 h-9 rounded-lg bg-black/5 hover:bg-black hover:text-white text-black/70 transition-all duration-200 flex items-center justify-center"
                      title={t('propertyListings.viewDetails')}
                    >
                      <i className="fas fa-arrow-right text-sm"></i>
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PropertyDetailModal: React.FC<{
  isOpen: boolean;
  property: Property | null;
  detailLoading: boolean;
  onClose: () => void;
}> = ({ isOpen, property, detailLoading, onClose }) => {
  if (!isOpen || !property) {
    return null;
  }

  const contract = property.contract;
  const tenantDisplay = contract?.tenant_name || (contract?.tenant_id ? `ID ${contract?.tenant_id}` : 'Chưa rõ');
  const price = contract?.monthly_payment || contract?.amount || property.price;
  const amenitiesList = transformAmenityData(property.amenities);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl max-h-[90vh] rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-xl font-semibold text-black">{getPropertyDisplayHeading(property)}</h3>
            <p className="text-sm text-gray-600">Thông tin hợp đồng và khách thuê hiện tại</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-black/60 hover:text-black transition-colors"
            aria-label="Close details popup"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {detailLoading && (
            <div className="rounded-2xl border border-gray-200 bg-yellow-50 p-4 text-sm text-gray-700">
              Đang tải thông tin chi tiết tài sản...
            </div>
          )}
          {!contract ? (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              Không có hợp đồng hiện tại cho tài sản này.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Khách thuê hiện tại</p>
                <p className="mt-2 text-base font-medium text-gray-900">{tenantDisplay}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tài sản</p>
                <p className="mt-2 text-base font-medium text-gray-900">{getPropertyDisplayHeading(property)}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Thời gian thuê</p>
                <p className="mt-2 text-sm text-gray-900">Bắt đầu: {contract.start_date || 'Chưa xác định'}</p>
                <p className="mt-1 text-sm text-gray-900">Kết thúc: {contract.end_date || 'Chưa xác định'}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Giá thuê</p>
                <p className="mt-2 text-base font-medium text-gray-900">{formatCurrency(price)}</p>
                {contract.amount && contract.monthly_payment && (
                  <p className="mt-1 text-sm text-gray-600">Tổng hợp đồng: {formatCurrency(contract.amount)}</p>
                )}
              </div>
            </div>
          )}

          {contract && (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Mã hợp đồng</p>
                  <p className="mt-2 text-sm text-gray-900">{contract.id}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Trạng thái</p>
                  <p className="mt-2 text-sm text-gray-900 capitalize">{contract.status || 'Không rõ'}</p>
                </div>
              </div>
              {contract.deposit != null && (
                <p className="mt-3 text-sm text-gray-700">Cọc: {formatCurrency(contract.deposit)}</p>
              )}
            </div>
          )}

          {amenitiesList.length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Tài sản đã thêm</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {amenitiesList.map((amenity, idx) => (
                  <div key={`${amenity.label}-${idx}`} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white border border-gray-200">
                    <div className="w-10 h-10 rounded-full bg-[#C4A35A]/10 text-[#C4A35A] flex items-center justify-center">
                      <i className={`${amenity.icon} text-base`}></i>
                    </div>
                    <div className="text-xs text-center text-black/80">{amenity.label}{amenity.count ? ` ×${amenity.count}` : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-[#C4A35A] px-5 py-2 text-sm font-semibold text-white hover:bg-black transition-colors"
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(value);
};

const getStatusBadge = (status: string, t: any) => {
  const badges: { [key: string]: { text: string; class: string } } = {
    'available': { text: t('propertyListings.status.available'), class: 'bg-[#C4A35A]/10 text-[#C4A35A] border border-[#C4A35A]/20' },
    'rented': { text: t('propertyListings.status.rented'), class: 'bg-blue-100 text-blue-800 border border-blue-200' },
    'pending': { text: t('propertyListings.status.pending'), class: 'bg-green-100 text-green-700 border border-green-200' },
    'inactive': { text: t('propertyListings.status.inactive'), class: 'bg-gray-100 text-gray-600 border border-gray-200' },
    'completed': { text: t('propertyListings.status.completed'), class: 'bg-primary/10 text-primary-hover border border-primary/20' },
    'expired': { text: t('propertyListings.status.expired'), class: 'bg-red-100 text-red-800 border border-red-200' }
  };
  return badges[status] || { text: status, class: 'bg-gray-100 text-gray-600 border border-gray-200' };
};

const hasUnexpiredRentalContract = (contract?: Property['contract']): boolean => {
  if (!contract?.id) return false;
  const status = (contract.status || '').toLowerCase();
  if (['cancelled', 'completed', 'expired'].includes(status)) return false;
  const endRaw = contract.end_date || contract.actual_end_date;
  if (!endRaw) return true;
  const endDate = new Date(endRaw);
  if (Number.isNaN(endDate.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return endDate >= today;
};

const PropertyListings: React.FC<PropertyListingsProps> = ({ accountType, ownerId, onPropertiesLoaded, isPublicProfile = false, searchQuery = '', hideOwnerToolbar = false, compactNative = false, ownerActionsRef }) => {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const { lastUpdated } = usePropertyContext();
  const { notifications, unreadCount, fetchNotifications, markAsRead } = useNotifications();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [contractModalMode, setContractModalMode] = useState<AddContractModalMode>('create');
  const [contractRequestPreset, setContractRequestPreset] = useState<ContractFormPreset | null>(null);
  const [contractPresetTitle, setContractPresetTitle] = useState<string | undefined>();
  const [acceptNotificationId, setAcceptNotificationId] = useState<number | null>(null);
  const [roomQRProperty, setRoomQRProperty] = useState<Property | null>(null);
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);
  const [isSearchInputOpen, setIsSearchInputOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isListPickerOpen, setIsListPickerOpen] = useState(false);
  const [listPickerBox, setListPickerBox] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'rented' | 'pending'>('all');
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [selectedListPropertyIds, setSelectedListPropertyIds] = useState<number[]>([]);
  const [savedLists, setSavedLists] = useState<SavedPropertyList[]>([]);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [selectedListForView, setSelectedListForView] = useState<SavedPropertyList | null>(null);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [activeListMenuId, setActiveListMenuId] = useState<string | null>(null);
  const [listMenuPos, setListMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [isListActionsOpen, setIsListActionsOpen] = useState(false);
  const [listActionsMode, setListActionsMode] = useState<'menu' | 'delete'>('menu');
  const [pendingDeleteListId, setPendingDeleteListId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement | null>(null);
  const listPickerRef = useRef<HTMLDivElement | null>(null);
  const listPickerMenuRef = useRef<HTMLDivElement | null>(null);
  const listActionMenuRef = useRef<HTMLDivElement | null>(null);
  const shownContractRequestIdsRef = useRef<Set<number>>(new Set());

  const handleOpenPropertyDetails = async (property: Property) => {
    setSelectedProperty(property);
    setIsDetailModalOpen(true);
    setDetailLoading(true);
    try {
      const response = await propertyApi.getPropertyById(property.id.toString());
      if (response.success && response.data) {
        const data = response.data as any;
        setSelectedProperty({
          ...property,
          id: typeof data.id === 'string' ? parseInt(data.id, 10) : data.id,
          title: data.title || property.title,
          type: data.type || property.type,
          price: data.price ?? property.price,
          area: data.area ?? property.area,
          bedrooms: data.bedrooms ?? property.bedrooms ?? 0,
          bathrooms: data.bathrooms ?? property.bathrooms ?? 0,
          status: data.status || property.status,
          main_image: data.main_image || property.main_image || property.gallery_images?.[0] || '/img/Product/Product (1).jpeg',
          gallery_images: data.gallery_images || property.gallery_images || [],
          created_at: data.created_at || property.created_at || '',
          address: data.address || property.address || '',
          full_address: data.full_address || property.full_address,
          street: data.street || property.street,
          ward_name: data.ward_name || property.ward_name,
          ward_code: data.ward_code || property.ward_code,
          province_name: data.province_name || property.province_name,
          province_code: data.province_code || property.province_code,
          room_number: data.room_number || property.room_number,
          pay_room_code: data.pay_room_code || property.pay_room_code,
          pay_url: data.pay_url ?? property.pay_url,
          amenities: data.amenities || (property as any).amenities,
          owner_id: data.owner_id ?? (data.owner?.id ? parseInt(String(data.owner.id), 10) : property.owner_id),
          owner: data.owner || property.owner,
          contract: property.contract || data.contract
        });
      }
    } catch (error) {
      console.error('Error loading detailed property data:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClosePropertyDetails = () => {
    setSelectedProperty(null);
    setIsDetailModalOpen(false);
    setDetailLoading(false);
  };

  const contractEligibleProperties = React.useMemo(
    () =>
      properties
        .filter((p) => !hasUnexpiredRentalContract(p.contract))
        .map((p) => ({
          id: p.id,
          title: p.title,
          price: p.price,
          room_number: p.room_number,
          full_address:
            p.full_address ||
            (typeof p.address === 'string' ? p.address : p.address?.full_address) ||
            p.street ||
            '',
        })),
    [properties],
  );

  const handleOpenCreateContract = () => {
    if (contractEligibleProperties.length === 0) {
      showAlert({
        type: 'warning',
        title: 'Không có phòng trống',
        message: 'Tất cả phòng đang hiển thị vẫn còn hợp đồng chưa hết hạn. Hủy hợp đồng hiện tại trước khi thêm mới.',
      });
      return;
    }
    setContractModalMode('create');
    setContractRequestPreset(null);
    setContractPresetTitle(undefined);
    setIsContractModalOpen(true);
  };

  const handleCloseContractModal = () => {
    setIsContractModalOpen(false);
    setContractModalMode('create');
    setContractRequestPreset(null);
    setContractPresetTitle(undefined);
    setAcceptNotificationId(null);
  };

  const openAcceptFromMetadata = useCallback((detail: Record<string, unknown>, notificationId?: number) => {
    if (isPublicProfile || accountType === 'tenant') return;
    const parsed = contractPresetFromRequestMetadata(detail);
    if (!parsed) return;

    const id =
      notificationId ??
      (typeof detail.notification_id === 'number' ? detail.notification_id : Number(detail.notification_id) || undefined);
    const key = id && Number.isFinite(id) ? id : null;

    setContractModalMode('accept');
    setContractRequestPreset(parsed.preset);
    setContractPresetTitle(parsed.title);
    setAcceptNotificationId(key);
    setIsContractModalOpen(true);
    if (key) {
      void markAsRead(key);
    }
  }, [accountType, isPublicProfile, markAsRead]);

  useEffect(() => {
    if (isPublicProfile || accountType === 'tenant') return;
    void fetchNotifications();
  }, [accountType, fetchNotifications, isPublicProfile]);

  useEffect(() => {
    if (isPublicProfile || accountType === 'tenant') return;
    if (unreadCount > 0) {
      void fetchNotifications();
    }
  }, [accountType, fetchNotifications, isPublicProfile, unreadCount]);

  useEffect(() => {
    if (isPublicProfile || accountType === 'tenant' || isContractModalOpen) return;

    const pending = notifications.find((notification) => {
      if (shownContractRequestIdsRef.current.has(notification.id)) return false;
      if (notification.status === NotificationStatus.READ) return false;
      return Boolean(getContractRequestMetadata(notification));
    });
    if (!pending) return;

    const metadata = getContractRequestMetadata(pending);
    if (!metadata) return;

    shownContractRequestIdsRef.current.add(pending.id);
    openAcceptFromMetadata(metadata, pending.id);
  }, [accountType, isContractModalOpen, isPublicProfile, notifications, openAcceptFromMetadata]);

  useEffect(() => {
    const handleContractRequest = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      if (!detail) return;
      const notificationId = typeof detail.notification_id === 'number' ? detail.notification_id : undefined;
      if (notificationId) {
        shownContractRequestIdsRef.current.add(notificationId);
      }
      openAcceptFromMetadata(detail, notificationId);
    };

    window.addEventListener(CONTRACT_REQUEST_EVENT, handleContractRequest);
    return () => window.removeEventListener(CONTRACT_REQUEST_EVENT, handleContractRequest);
  }, [openAcceptFromMetadata]);

  const handleShowRoomQR = (property: Property) => {
    setRoomQRProperty(property);
  };

  const handleRoomQRUpdated = useCallback((data: { pay_room_code?: string; pay_url?: string | null }) => {
    setRoomQRProperty((current) => {
      if (!current) return current;
      const updated = { ...current, ...data };
      setProperties((list) =>
        list.map((p) => (p.id === current.id ? { ...p, ...data } : p))
      );
      return updated;
    });
  }, []);

  const handleContractAdded = (contract: Contract) => {
    if (acceptNotificationId) {
      void markAsRead(acceptNotificationId);
    }
    setIsContractModalOpen(false);
    setContractModalMode('create');
    setContractRequestPreset(null);
    setContractPresetTitle(undefined);
    setAcceptNotificationId(null);
    const propertyId = contract.property_id;
    if (propertyId) {
      setProperties((currentProperties) =>
        currentProperties.map((currentProperty) =>
          currentProperty.id === propertyId ? { ...currentProperty, contract } : currentProperty,
        ),
      );
    }
    requestDashboardRefresh();
  };

  const handleDeleteProperty = async (property: Property) => {
    if (hasUnexpiredRentalContract(property.contract)) {
      showAlert({
        type: 'warning',
        title: t('propertyListings.delete'),
        message: t('propertyListings.cannotDeleteWithContract'),
      });
      return;
    }

    const confirmed = window.confirm(t('propertyListings.confirmDelete'));
    if (!confirmed) return;

    const result = await propertyApi.deleteProperty(property.id);
    if (!result.success) {
      showAlert({
        type: 'error',
        title: t('propertyListings.delete'),
        message: result.message || t('propertyListings.cannotDeleteWithContract'),
      });
      return;
    }

    setProperties((current) => current.filter((item) => item.id !== property.id));
    requestDashboardRefresh();
    showAlert({
      type: 'success',
      title: t('propertyListings.delete'),
      message: t('propertyListings.deleteSuccess'),
    });
  };

  useEffect(() => {
    loadProperties();
  }, [accountType, user, ownerId, lastUpdated]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const lists = await fetchSavedPropertyLists();
      if (!cancelled) setSavedLists(lists);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Thông báo khi properties được load thành công
  useEffect(() => {
    if (!loading && !error && properties.length >= 0 && onPropertiesLoaded) {
      onPropertiesLoaded(properties.length > 0);
    }
  }, [properties, loading, error, onPropertiesLoaded]);

  useEffect(() => {
    setSearchInputValue(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (isSearchInputOpen) {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }
  }, [isSearchInputOpen]);

  useEffect(() => {
    if (!isListPickerOpen) {
      setListPickerBox(null);
      return;
    }

    const updateListPickerBox = () => {
      const trigger = listPickerRef.current?.querySelector('button');
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const supportEl = document.querySelector('[title="Hỗ trợ"]');
      const supportTop = supportEl
        ? supportEl.getBoundingClientRect().top
        : (window.visualViewport?.height ?? window.innerHeight) - 132;
      const top = rect.bottom + 8;
      setListPickerBox({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(160, Math.floor(supportTop - top - 8)),
      });
    };

    updateListPickerBox();
    window.addEventListener('resize', updateListPickerBox);
    window.addEventListener('scroll', updateListPickerBox, true);
    window.visualViewport?.addEventListener('resize', updateListPickerBox);
    window.visualViewport?.addEventListener('scroll', updateListPickerBox);
    return () => {
      window.removeEventListener('resize', updateListPickerBox);
      window.removeEventListener('scroll', updateListPickerBox, true);
      window.visualViewport?.removeEventListener('resize', updateListPickerBox);
      window.visualViewport?.removeEventListener('scroll', updateListPickerBox);
    };
  }, [isListPickerOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
      if (
        listPickerRef.current &&
        !listPickerRef.current.contains(event.target as Node) &&
        !listPickerMenuRef.current?.contains(event.target as Node)
      ) {
        setIsListPickerOpen(false);
      }

      if (
        activeListMenuId &&
        !(event.target as HTMLElement).closest('[data-list-menu-toggle]') &&
        listActionMenuRef.current &&
        !listActionMenuRef.current.contains(event.target as Node)
      ) {
        setActiveListMenuId(null);
        setListMenuPos(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeListMenuId]);

  useEffect(() => {
    if (!activeListMenuId) return;
    const closeListMenu = () => {
      setActiveListMenuId(null);
      setListMenuPos(null);
    };
    window.addEventListener('scroll', closeListMenu, true);
    window.addEventListener('resize', closeListMenu);
    return () => {
      window.removeEventListener('scroll', closeListMenu, true);
      window.removeEventListener('resize', closeListMenu);
    };
  }, [activeListMenuId]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      
      if (accountType === 'tenant') {
        // Load rental properties for tenant (from contracts AND orders)
        // Use ownerId prop if provided (for public profiles), otherwise use current user
        const currentUserId = ownerId || (user?.id ? parseInt(user.id) : null);
        
        if (!currentUserId) {
          console.warn('⚠️ No user ID found, cannot filter tenant contracts/orders');
          setProperties([]);
          return;
        }

        // 1. Load contracts - Backend already filters by tenant role
        const allContractsResponse = await contractApi.getContracts({ role: 'tenant' }) as any;
        const allContracts: ContractSummary[] = Array.isArray(allContractsResponse) 
          ? allContractsResponse 
          : allContractsResponse?.contracts || [];
        
        // Filter by status - backend already filtered by tenant_id
        // Include: active, pending, draft, completed listings
        const rentalContracts = allContracts.filter(
          (contract: ContractSummary) => 
            ['active', 'pending', 'draft', 'completed', 'expired'].includes(contract.status)
        );

        // Get order IDs that already have contracts
        const orderIdsWithContracts = new Set(
          rentalContracts
            .map((c: any) => c.order_id)
            .filter((id: any) => id != null)
        );

        // 2. Load orders that are paid but don't have contracts yet
        const allOrders = await orderApi.getOrders({ limit: 100 });
        
        // Filter orders: paid orders without contracts
        const ordersWithoutContracts = allOrders.filter((order: any) => {
          const isPaid = order.payment_status === 'completed' || order.payment_status === 'paid';
          const isConfirmed = order.status === 'confirmed' || order.status === 'completed';
          const belongsToUser = order.customer_id === currentUserId;
          const hasNoContract = !orderIdsWithContracts.has(order.id);
          const hasProperty = !!order.property;
          
          return isPaid && (isConfirmed || belongsToUser) && hasNoContract && hasProperty;
        });

        // 3. Check payments for each contract
        const contractsWithPayments = await Promise.all(
          rentalContracts.map(async (contract: ContractSummary) => {
            try {
              const payments = await contractApi.getContractPayments();
              const hasDepositPayment = payments.some(
                payment => payment.payment_status === 'completed' && payment.amount > 0
              );
              return { contract, hasDepositPayment, payments };
            } catch (err) {
              return { contract, hasDepositPayment: false, payments: [] };
            }
          })
        );

        // Only show contracts with payments or active/completed/expired status
        const validContracts = contractsWithPayments.filter(
          ({ contract, hasDepositPayment }: { contract: ContractSummary; hasDepositPayment: boolean; payments: any[] }) => 
            ['active', 'completed', 'expired'].includes(contract.status) || hasDepositPayment
        );

        // 4. Load property details for each contract
        const propertiesWithContracts: Property[] = [];
        
        for (const { contract, payments } of validContracts) {
          if (contract.property_id) {
            try {
              const propertyResponse = await propertyApi.getPropertyById(contract.property_id.toString());
              if (propertyResponse.success && propertyResponse.data) {
                const propertyData = propertyResponse.data;
                console.log(`🏠 [PropertyListings] Loaded property ${propertyData.id}:`, {
                  title: propertyData.title,
                  room_number: propertyData.room_number,
                  area: propertyData.area
                });
                
                const propertyOwnerId = (propertyData as any).owner_id || (propertyData as any).owner?.id;
                if (propertyOwnerId && parseInt(String(propertyOwnerId)) === currentUserId) {
                  console.log(`⚠️ Skipping property ${propertyData.id} - user owns this property`);
                  continue;
                }
                
                const totalPaid = payments
                  .filter((p: any) => p.payment_status === 'completed')
                  .reduce((sum: number, p: any) => sum + p.amount, 0);

                let contractStatus = 'Đang Thuê';
                if ((contract.status === 'pending' || contract.status === 'draft') && totalPaid > 0) {
                  contractStatus = 'Đang Đặt Thuê';
                } else if (contract.status === 'completed') {
                  contractStatus = 'Đã Hoàn Thành';
                } else if (contract.status === 'expired') {
                  contractStatus = 'Hết Hạn';
                }

                propertiesWithContracts.push({
                  id: parseInt(propertyData.id),
                  title: propertyData.title,
                  type: propertyData.type,
                  price: contract.monthly_payment || contract.amount,
                  area: propertyData.area,
                  bedrooms: propertyData.bedrooms || 0,
                  bathrooms: propertyData.bathrooms || 0,
                  status: contract.status,
                  main_image: propertyData.main_image || propertyData.images?.[0] || '/img/Product/Product (1).jpeg',
                  gallery_images: propertyData.gallery_images || propertyData.images,
                  created_at: propertyData.created_at,
                  address: propertyData.address || propertyData.location,
                  full_address: propertyData.full_address,
                  street: propertyData.street,
                  ward_name: propertyData.ward_name,
                  ward_code: propertyData.ward_code,
                  province_name: propertyData.province_name,
                  province_code: propertyData.province_code,
                  room_number: propertyData.room_number || undefined,
                  pay_room_code: (propertyData as any).pay_room_code || undefined,
                  pay_url: (propertyData as any).pay_url || undefined,
                  contract: {
                    ...contract,
                    totalPaid: totalPaid > 0 ? totalPaid : undefined, // Only include if > 0
                    payments
                  } as Contract & { totalPaid?: number; payments: any[] },
                  contractStatus
                });
                
                console.log(`✅ [PropertyListings] Pushed property to list:`, {
                  id: parseInt(propertyData.id),
                  title: propertyData.title,
                  room_number: propertyData.room_number
                });
              }
            } catch (err) {
              console.error(`Error loading property ${contract.property_id}:`, err);
            }
          }
        }

        // 5. Load property details for orders without contracts
        const propertiesFromOrders: Property[] = [];
        
        for (const order of ordersWithoutContracts) {
          if (order.property_id && order.property) {
            try {
              const propertyData = order.property;
              const propertyOwnerId = propertyData.owner?.id;
              
              if (propertyOwnerId && parseInt(String(propertyOwnerId)) === currentUserId) {
                console.log(`⚠️ Skipping property ${propertyData.id} from order - user owns this property`);
                continue;
              }

              // Calculate total paid from order - use actual paid amount from transactions, not total_amount
              // total_amount is the total order amount, not what was actually paid
              const totalPaid = order.total_paid || 0;  // Use actual paid amount from backend

              propertiesFromOrders.push({
                id: typeof propertyData.id === 'string' ? parseInt(propertyData.id) : Number(propertyData.id),
                title: propertyData.title || 'N/A',
                type: propertyData.type || 'apartment',
                price: order.monthly_price || order.total_rental_amount / order.duration_months,
                area: propertyData.area || 0,
                bedrooms: propertyData.bedrooms || 0,
                bathrooms: propertyData.bathrooms || 0,
                status: 'pending', // Order status
                main_image: propertyData.images?.[0] || '/img/Product/Product (1).jpeg',
                gallery_images: propertyData.images || [],
                created_at: order.created_at,
                address: propertyData.location || '',
                full_address: propertyData.location || '',
                street: '',
                ward_name: '',
                ward_code: '',
                province_name: '',
                province_code: '',
                contract: {
                  id: 0, // No contract yet
                  order_id: order.id,
                  property_id: order.property_id,
                  tenant_id: order.customer_id,
                  landlord_id: order.landlord_id,
                  type: 'rental',
                  status: 'pending',
                  amount: order.total_rental_amount,
                  monthly_payment: order.monthly_price,
                  deposit: order.deposit_amount,
                  totalPaid: totalPaid > 0 ? totalPaid : undefined, // Only include if > 0
                  payments: []
                } as any,
                contractStatus: 'Đang Đặt Thuê' // Order paid but contract not created yet
              });
            } catch (err) {
              console.error(`Error loading property from order ${order.id}:`, err);
            }
          }
        }

        // 6. Combine properties from contracts and orders
        const allProperties = [...propertiesWithContracts, ...propertiesFromOrders];
        
        // Remove duplicates by property_id (in case order has contract)
        const uniqueProperties = allProperties.reduce((acc: Property[], current: Property) => {
          const existing = acc.find(p => p.id === current.id);
          if (!existing) {
            acc.push(current);
          }
          return acc;
        }, []);

        console.log(`📋 [PropertyListings] Final properties loaded (Tenant):`, uniqueProperties.map(p => ({
          id: p.id,
          title: p.title,
          room_number: p.room_number
        })));

        setProperties(uniqueProperties);
      } else {
        // Load properties for landlord/broker/business
        // For public profiles, use ownerId directly without requiring current user
        let targetUserId: number | null = null;

        if (isPublicProfile && ownerId) {
          // Public profile: use the provided ownerId
          targetUserId = ownerId;
        } else {
          // Current user's own profile: get current user ID
          const currentUserId = user?.id ? parseInt(user.id) : null;
          
          if (!currentUserId) {
            console.warn('⚠️ No user ID found, cannot load properties');
            setProperties([]);
            return;
          }
          targetUserId = currentUserId;
        }
        console.log('🏠 [PropertyListings] Loading properties for user:', targetUserId, user?.email || user?.name);
        const response = await propertyApi.getProperties({ 
          owner_id: targetUserId,  // Filter on backend side for better performance
          // Không truyền status để lấy tất cả status (available, rented, pending, etc.)
          limit: 100 
        });
        
        console.log('🏠 [PropertyListings] API response:', {
          success: response.success,
          total: response.data?.length || 0,
          totalFromAPI: response.total || 0
        });
        
        if (response.success && response.data) {
          // Backend already filtered by owner_id, so response.data contains only user's properties
          const userProperties = response.data;
          
          console.log('🏠 [PropertyListings] Properties loaded from backend (already filtered):', userProperties.length);
          
          if (userProperties.length > 0) {
            console.log('✅ [PropertyListings] Sample property:', {
              id: userProperties[0].id,
              title: userProperties[0].title,
              owner_id: (userProperties[0] as any).owner_id
            });
          } else {
            console.log('ℹ️ [PropertyListings] User chưa có properties nào. Cần đăng properties trước.');
          }

          // Try to load tracking stats (optional, only for own profile, skip for public profiles)
          let trackingData: any[] = [];
          if (!isPublicProfile) {
            try {
              const trackingRes = await axios.get(`${API_BASE_URL}/tracking/landlord-properties-stats`, {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              const responseData = (trackingRes as any)?.data;
              trackingData = Array.isArray(responseData) ? responseData : responseData?.data || [];
            } catch (trackingErr) {
              console.warn('⚠️ Could not load tracking stats (optional):', trackingErr);
              // Continue without tracking stats
            }
          }

          // Load contracts for all properties to determine actual rental status
          const propertiesWithContracts = await Promise.all(
            userProperties.map(async (prop: any) => {
              try {
                const propertyContracts = await contractApi.getContractsByProperty(parseInt(prop.id));
                
                // Find the active or pending contract (including manual entries)
                const currentContract = propertyContracts.contracts.find(
                  (c: Contract) => ['active', 'pending', 'draft'].includes(c.status)
                ) || null;
                const hasActiveContract = !!currentContract;
                
                // Có hợp đồng đang hiệu lực → trạng thái "đã cho thuê"
                const actualStatus = hasActiveContract ? 'rented' : (prop.status || 'available');
                
                if (hasActiveContract) {
                  console.log(`✅ [PropertyListings] Property ${prop.id} marked as 'rented' (contract ${currentContract?.id}, workflow: ${currentContract?.workflow_current_step})`);
                }
                
                return {
                  property: prop,
                  actualStatus,
                  contract: currentContract || null
                };
              } catch (err) {
                console.warn(`⚠️ Error loading contracts for property ${prop.id}:`, err);
                return {
                  property: prop,
                  actualStatus: prop.status || 'available',
                  contract: null
                };
              }
            })
          );

          // Merge properties with tracking data, actual status, and contract info
          const mergedProperties = propertiesWithContracts.map(({ property: prop, actualStatus, contract }: any) => {
            const stats = trackingData.find((t: any) => t.id === prop.id || t.id === parseInt(prop.id));
            return {
              id: parseInt(prop.id),
              title: prop.title || prop.name || 'Bất động sản',
              type: prop.type,
              price: prop.price,
              rent_payment_day: prop.rent_payment_day ?? prop.rentPaymentDay ?? contract?.rent_payment_day,
              area: prop.area,
              bedrooms: prop.bedrooms || 0,
              bathrooms: prop.bathrooms || 0,
              status: actualStatus, // Use actual status based on contracts
              main_image: prop.main_image || prop.images?.[0] || '/img/Product/Product (1).jpeg',
              gallery_images: prop.gallery_images || prop.images || [],
              created_at: prop.created_at,
              // Address fields - pass all available address information
              address: prop.address || prop.location,
              full_address: prop.full_address,
              street: prop.street,
              ward_name: prop.ward_name,
              ward_code: prop.ward_code,
              room_number: prop.room_number || undefined,
              pay_room_code: prop.pay_room_code || undefined,
              pay_url: prop.pay_url || undefined,
              // Contract information
              contract: contract || undefined,
              province_name: prop.province_name,
              province_code: prop.province_code,
              // Owner information from API response
              owner_id: prop.owner_id,
              owner: prop.owner ? {
                id: prop.owner.id,
                full_name: prop.owner.full_name,
                username: prop.owner.username,
                avatar: prop.owner.avatar,
                is_verified: prop.owner.is_verified || false,
                email: prop.owner.email
              } : undefined,
              total_views: stats?.total_views || 0,
              total_favorites: stats?.total_favorites || 0,
              total_contacts: stats?.total_contacts || 0,
              unique_viewers: stats?.unique_viewers || 0
            } as Property;
          });

          console.log(`📋 [PropertyListings] Final properties loaded (Landlord/Broker):`, mergedProperties.map(p => ({
            id: p.id,
            title: p.title,
            room_number: p.room_number
          })));

          setProperties(mergedProperties);
        } else {
          setProperties([]);
        }
      }
    } catch (err: any) {
      console.error('❌ Error loading properties:', err);
      
      // Extract error message properly - handle both string and object
      let errorMessage = 'Không thể tải danh sách bất động sản';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Handle validation errors (422) - errorData.detail might be an array
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Validation errors array
            errorMessage = errorData.detail.map((e: any) => 
              typeof e === 'string' ? e : e.msg || JSON.stringify(e)
            ).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else {
            // If detail is an object, just show generic message
            errorMessage = 'Dữ liệu không hợp lệ. Vui lòng thử lại.';
          }
        } else if (errorData.message) {
          errorMessage = typeof errorData.message === 'string' 
            ? errorData.message 
            : 'Không thể tải danh sách bất động sản';
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const activeList = savedLists.find((list) => list.id === activeListId) ?? null;
  const allPropertiesList = {
    id: '__all__',
    name: 'Tất cả',
    propertyIds: properties.map((property) => property.id),
    createdAt: ''
  };
  const visibleLists = [allPropertiesList, ...savedLists];
  const filteredProperties = filterPropertiesBySearchQueryAndStatusAndList(
    properties,
    searchInputValue,
    statusFilter,
    activeList?.propertyIds ?? null
  );
  // Auto-scroll to properties list when search results change
  useEffect(() => {
    if (searchInputValue.trim() && filteredProperties.length > 0) {
      const propertiesListElement = document.getElementById('properties-list');
      if (propertiesListElement) {
        propertiesListElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }
  }, [searchInputValue, filteredProperties.length]);

  const handleToggleSearch = () => {
    setIsSearchInputOpen((prev) => !prev);
  };

  const handleOpenCreateList = () => {
    setSelectedListForView(null);
    setEditingListId(null);
    setListName('');
    setSelectedListPropertyIds([]);
    setActiveListMenuId(null);
    setIsListModalOpen(true);
  };

  const handleOpenListActions = () => {
    setListActionsMode('menu');
    setPendingDeleteListId(null);
    setIsListActionsOpen(true);
  };

  const closeListActions = () => {
    setIsListActionsOpen(false);
    setListActionsMode('menu');
    setPendingDeleteListId(null);
  };

  if (ownerActionsRef) {
    ownerActionsRef.current = {
      openCreateContract: handleOpenCreateContract,
      openCreateList: handleOpenCreateList,
      openListActions: handleOpenListActions,
    };
  }

  const handleStartEditingList = (list: SavedPropertyList) => {
    setSelectedListForView(null);
    setEditingListId(list.id);
    setListName(list.name);
    setSelectedListPropertyIds(list.propertyIds);
    setActiveListMenuId(null);
    setListMenuPos(null);
    setIsListModalOpen(true);
  };

  const handlePickVisibleList = (list: SavedPropertyList) => {
    setActiveListId(list.id === '__all__' ? null : list.id);
    setActiveListMenuId(null);
    setListMenuPos(null);
  };

  const handleOpenListView = (list: SavedPropertyList) => {
    setSelectedListForView(list);
    setEditingListId(list.id);
    setListName(list.name);
    setSelectedListPropertyIds(list.propertyIds);
    setActiveListMenuId(null);
  };

  const toggleSelectedListProperty = (propertyId: number) => {
    const isAlreadyAssignedToAnotherList = savedLists.some((list) => {
      if (editingListId && list.id === editingListId) {
        return false;
      }
      return list.propertyIds.includes(propertyId);
    });

    if (isAlreadyAssignedToAnotherList) {
      return;
    }

    setSelectedListPropertyIds((prev) =>
      prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
    );
  };

  const handleSaveList = () => {
    const trimmedName = listName.trim();
    if (!trimmedName) {
      showAlert({ type: 'error', title: 'Thiếu tên danh sách', message: 'Vui lòng nhập tên danh sách trước khi lưu.' });
      return;
    }

    const nextLists = editingListId
      ? savedLists.map((list) => (list.id === editingListId ? { ...list, name: trimmedName, propertyIds: selectedListPropertyIds } : list))
      : [createSavedPropertyList(trimmedName, selectedListPropertyIds), ...savedLists];

    setSavedLists(nextLists);
    void persistSavedPropertyLists(nextLists);
    setIsListModalOpen(false);
    setEditingListId(null);
    setListName('');
    setSelectedListPropertyIds([]);
    setSelectedListForView(null);
    showAlert({ type: 'success', title: editingListId ? 'Đã cập nhật danh sách' : 'Đã tạo danh sách', message: `Danh sách "${trimmedName}" đã được ${editingListId ? 'cập nhật' : 'lưu'}.` });
  };

  const handleDeleteList = (listId: string) => {
    const nextLists = savedLists.filter((list) => list.id !== listId);
    setSavedLists(nextLists);
    void persistSavedPropertyLists(nextLists);
    if (selectedListForView?.id === listId) {
      setSelectedListForView(null);
    }
    if (activeListId === listId) {
      setActiveListId(null);
    }
    setIsListModalOpen(false);
    setEditingListId(null);
    setListName('');
    setSelectedListPropertyIds([]);
    showAlert({ type: 'success', title: 'Đã xóa danh sách', message: 'Danh sách đã được xóa thành công.' });
  };

  const getPropertyListSubtitle = (property: Property) => {
    const parts = [getPropertyTypeLabel(property.type || 'apartment')];
    if (property.room_number) {
      parts.push(`Căn ${property.room_number}`);
    }
    return parts.join(' • ');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 text-center border border-black/5">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-[#C4A35A] border-t-transparent mx-auto mb-3"></div>
        <p className="text-sm text-black/60">{t('propertyListings.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 border border-black/5">
        <div className="flex items-center gap-3 mb-3">
          <i className="fas fa-exclamation-circle text-black text-xl"></i>
          <h3 className="text-base font-semibold text-black">{t('propertyListings.errorLoading')}</h3>
        </div>
        <p className="text-sm text-black/60 mb-4">{error}</p>
        <button
          onClick={loadProperties}
          className="px-4 py-2 bg-[#C4A35A] text-white rounded-lg hover:bg-black transition-colors text-sm"
        >
          <i className="fas fa-redo mr-2 text-xs"></i>
          {t('propertyListings.retry')}
        </button>
      </div>
    );
  }

  if (properties.length === 0) {
    const isTenant = accountType === 'tenant';
    return (
      <div className="bg-white rounded-xl p-6 sm:p-8 border border-black/5 text-center mx-0 sm:mx-6">
        <i className={`fas ${isTenant ? 'fa-home' : 'fa-building'} text-4xl text-[#C4A35A]/40 mb-4`}></i>
        <h3 className="text-base font-semibold text-black mb-2">
          {isTenant ? t('propertyListings.noRentedProperties') : t('propertyListings.noProperties')}
        </h3>
        <p className="text-sm text-black/60 mb-5">
          {isTenant ? t('propertyListings.noRentedPropertiesMessage') : t('propertyListings.noPropertiesMessage')}
        </p>
        {!isTenant && !isPublicProfile && (
          <Link
            to="/add-product"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C4A35A] text-white rounded-lg hover:bg-black transition-colors text-sm font-medium"
          >
            <i className="fas fa-plus text-xs"></i>
            {t('propertyListings.addProperty')}
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5 -mx-3 sm:-mx-6">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-6 py-4 gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="hidden text-lg font-semibold text-black sm:block">
              {accountType === 'tenant' ? t('propertyListings.rentedProperties') : t('propertyListings.myProperties')}
            </h2>
          </div>
          {searchInputValue.trim() && (
            <p className="text-sm text-black/60 mt-1 truncate">
              Tìm thấy {filteredProperties.length} kết quả cho "{searchInputValue.trim()}"
            </p>
          )}
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
          {accountType !== 'tenant' && !isPublicProfile && !hideOwnerToolbar && (
            <>
              <button
                type="button"
                onClick={handleOpenCreateContract}
                className="rounded-full border border-[#C4A35A]/30 bg-white px-3 py-2 text-xs font-semibold text-[#C4A35A] shadow-sm transition-all hover:bg-[#C4A35A]/10"
              >
                <i className="fas fa-file-contract mr-1.5"></i>
                Tạo hợp đồng
              </button>
              <button
                type="button"
                onClick={handleOpenCreateList}
                className="rounded-full border border-[#C4A35A]/30 bg-[#C4A35A] px-3 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-black"
              >
                <i className="fas fa-plus mr-1.5"></i>
                Tạo danh sách
              </button>
            </>
          )}
          {accountType !== 'tenant' && !isPublicProfile && (
            <div className="relative" ref={statusDropdownRef}>
              <button
                type="button"
                onClick={() => setIsStatusDropdownOpen((prev) => !prev)}
                className="rounded-full border border-[#C4A35A]/30 bg-white px-3 py-2 text-xs font-semibold text-[#C4A35A] shadow-sm transition-all hover:bg-[#C4A35A]/10"
                title="Lọc theo tình trạng cho thuê"
              >
                {statusFilter === 'all' ? 'Tất cả' : statusFilter === 'pending' ? 'Đang chờ thuê' : 'Đã cho thuê'}
              </button>
              {isStatusDropdownOpen && (
                <div className="absolute right-0 z-20 mt-2 w-44 rounded-xl border border-black/10 bg-white p-2 shadow-lg">
                  <button
                    type="button"
                    onClick={() => { setStatusFilter('all'); setIsStatusDropdownOpen(false); }}
                    className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-black hover:bg-[#C4A35A]/10"
                  >
                    Tất cả
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStatusFilter('pending'); setIsStatusDropdownOpen(false); }}
                    className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-black hover:bg-[#C4A35A]/10"
                  >
                    Đang chờ thuê
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStatusFilter('rented'); setIsStatusDropdownOpen(false); }}
                    className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-black hover:bg-[#C4A35A]/10"
                  >
                    Đã cho thuê
                  </button>
                </div>
              )}
            </div>
          )}
          {isSearchInputOpen && (
            <div className="flex items-center gap-2 rounded-full border border-[#C4A35A]/30 bg-white px-3 py-2 shadow-sm">
              <i className="fas fa-search text-[#C4A35A] text-sm"></i>
              <input
                ref={searchInputRef}
                type="text"
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                placeholder="Tìm theo tên BĐS"
                className="w-40 sm:w-56 border-none bg-transparent text-sm text-black outline-none"
              />
              {searchInputValue.trim() && (
                <button
                  type="button"
                  onClick={() => setSearchInputValue('')}
                  className="text-black/40 hover:text-black"
                  title="Xóa tìm kiếm"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
          )}
          {accountType !== 'tenant' && !isPublicProfile && (
            <button
              type="button"
              onClick={handleToggleSearch}
              className="w-9 h-9 rounded-full bg-[#C4A35A] hover:bg-black text-white transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md flex-shrink-0 border-none"
              title="Tìm kiếm theo tên BĐS"
            >
              <i className="fas fa-search text-sm"></i>
            </button>
          )}
        </div>
      </div>

      {isListActionsOpen && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="Đóng"
            onClick={closeListActions}
          />
          <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl border-2 border-[#D8B86A] bg-gradient-to-b from-[#FFFDF8] to-[#F8EFD8] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#D8B86A]/60 bg-[#2B2520] px-4 py-3">
              <h2 className="text-base font-bold text-white">
                {listActionsMode === 'delete' ? 'Xóa danh sách' : 'Tạo/ Xóa danh sách'}
              </h2>
              <button
                type="button"
                onClick={closeListActions}
                className="rounded-xl bg-[#C4A35A] px-3 py-1.5 text-sm font-semibold text-white"
              >
                Đóng
              </button>
            </div>

            {listActionsMode === 'menu' ? (
              <div className="space-y-2 p-3">
                <button
                  type="button"
                  onClick={() => {
                    closeListActions();
                    handleOpenCreateList();
                  }}
                  className="flex w-full items-center rounded-xl border border-[#D8B86A] bg-white/70 px-3 py-3 text-left text-sm font-medium text-black"
                >
                  <i className="fas fa-plus mr-2.5 text-[#C4A35A]" />
                  Tạo danh sách
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPendingDeleteListId(null);
                    setListActionsMode('delete');
                  }}
                  className="flex w-full items-center rounded-xl border border-red-200 bg-white/70 px-3 py-3 text-left text-sm font-medium text-red-600"
                >
                  <i className="fas fa-trash mr-2.5" />
                  Xóa danh sách
                </button>
              </div>
            ) : (
              <div className="p-3">
                {savedLists.length === 0 ? (
                  <p className="rounded-xl bg-white/70 px-3 py-4 text-center text-sm text-black/50">
                    Chưa có danh sách nào.
                  </p>
                ) : (
                  <div className="max-h-[50vh] space-y-2 overflow-y-auto">
                    {savedLists.map((list) => {
                      const confirming = pendingDeleteListId === list.id;
                      return (
                        <div
                          key={list.id}
                          className="flex items-center justify-between gap-2 rounded-xl border border-black/10 bg-white/80 px-3 py-2.5"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-black">{list.name}</p>
                            <p className="text-[11px] text-black/45">{list.propertyIds.length} mục</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (!confirming) {
                                setPendingDeleteListId(list.id);
                                return;
                              }
                              handleDeleteList(list.id);
                              setPendingDeleteListId(null);
                            }}
                            className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold ${
                              confirming ? 'bg-red-600 text-white' : 'border border-red-200 text-red-600'
                            }`}
                          >
                            {confirming ? 'Xác nhận xóa' : 'Xóa'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setPendingDeleteListId(null);
                    setListActionsMode('menu');
                  }}
                  className="mt-3 w-full rounded-xl px-3 py-2 text-sm font-medium text-black/60 hover:bg-black/[0.03]"
                >
                  <i className="fas fa-arrow-left mr-1.5" />
                  Quay lại
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isListModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-black">{selectedListForView ? selectedListForView.name : editingListId ? 'Chỉnh sửa danh sách' : 'Tạo danh sách mới'}</h3>
                <p className="text-sm text-black/60">
                  {selectedListForView ? 'Các bất động sản trong danh sách này' : 'Nhập tên danh sách trước, sau đó chọn các bất động sản để thêm vào.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsListModalOpen(false);
                  setSelectedListForView(null);
                  setEditingListId(null);
                  setListName('');
                  setSelectedListPropertyIds([]);
                }}
                className="text-black/60 transition-colors hover:text-black"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            {selectedListForView ? (
              <div className="space-y-4 p-5">
                <div className="flex items-center justify-between gap-2 rounded-2xl border border-[#C4A35A]/20 bg-[#C4A35A]/5 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-black">{selectedListForView.name}</p>
                    <p className="text-xs text-black/60">{selectedListForView.propertyIds.length} sản phẩm trong danh sách</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleStartEditingList(selectedListForView)}
                    className="rounded-full border border-[#C4A35A]/20 bg-white px-3 py-2 text-sm font-medium text-[#C4A35A] hover:bg-[#C4A35A]/10"
                  >
                    Chỉnh sửa
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  {selectedListForView.propertyIds.length === 0 ? (
                    <div className="md:col-span-2 rounded-2xl border border-dashed border-gray-200 p-4 text-sm text-black/60">
                      Danh sách này chưa có bất động sản nào.
                    </div>
                  ) : (
                    selectedListForView.propertyIds.map((propertyId) => {
                      const property = properties.find((item) => item.id === propertyId);
                      if (!property) return null;
                      return (
                        <div key={property.id} className="rounded-2xl border border-gray-200 p-3">
                          <p className="text-sm font-semibold text-black">{property.full_address || property.address?.full_address || property.address?.street || getPropertyDisplayHeading(property)}</p>
                          <p className="mt-1 text-xs text-black/60">{getPropertyListSubtitle(property)}</p>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4 p-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-black">Tên danh sách</label>
                  <input
                    type="text"
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                    placeholder="Ví dụ: Nhà đầu tư, Căn hộ đẹp..."
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-black outline-none focus:border-[#C4A35A]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">Chọn bất động sản</label>
                  <div className="max-h-72 space-y-2 overflow-auto rounded-2xl border border-gray-200 p-3">
                    {properties.length === 0 ? (
                      <p className="text-sm text-black/60">Chưa có bất động sản nào để chọn.</p>
                    ) : (
                      properties.map((property) => {
                        const isSelected = selectedListPropertyIds.includes(property.id);
                        const isDisabled = savedLists.some((list) => {
                          if (editingListId && list.id === editingListId) {
                            return false;
                          }
                          return list.propertyIds.includes(property.id);
                        });

                        return (
                          <label
                            key={property.id}
                            className={`flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-black">{property.full_address || property.address?.full_address || property.address?.street || getPropertyDisplayHeading(property)}</p>
                              <p className="text-xs text-black/60">{getPropertyListSubtitle(property)}</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectedListProperty(property.id)}
                              disabled={isDisabled}
                              className="h-4 w-4 rounded border-gray-300 text-[#C4A35A] focus:ring-[#C4A35A]"
                            />
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsListModalOpen(false);
                      setEditingListId(null);
                      setListName('');
                      setSelectedListPropertyIds([]);
                      setSelectedListForView(null);
                    }}
                    className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-black hover:bg-gray-50"
                  >
                    Hủy
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveList}
                    className="rounded-full bg-[#C4A35A] px-4 py-2 text-sm font-semibold text-white hover:bg-black"
                  >
                    {editingListId ? 'Lưu thay đổi' : 'Tạo danh sách'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {visibleLists.length > 0 && (
        <div className="relative mb-3 px-3 lg:hidden" ref={listPickerRef}>
          <button
            type="button"
            onClick={() => setIsListPickerOpen((open) => !open)}
            className="flex w-full items-center justify-between rounded-full border border-[#C4A35A]/30 bg-[#C4A35A] px-4 py-2.5 text-left text-white shadow-sm"
          >
            <span className="min-w-0 truncate text-sm font-semibold">
              {activeList?.name ?? 'Tất cả'}
              <span className="ml-2 text-xs font-medium text-white/80">
                {activeList ? activeList.propertyIds.length : properties.length} mục
              </span>
            </span>
            <i className={`fas fa-chevron-down text-xs transition-transform ${isListPickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {isListPickerOpen && listPickerBox && createPortal(
            <div
              ref={listPickerMenuRef}
              className="fixed z-[70] overflow-y-auto overscroll-contain rounded-2xl border border-black/10 bg-white p-2 shadow-xl"
              style={{
                top: listPickerBox.top,
                left: listPickerBox.left,
                width: listPickerBox.width,
                maxHeight: listPickerBox.maxHeight,
              }}
            >
              {visibleLists.map((list) => {
                const isSpecialAll = list.id === '__all__';
                const isActive = isSpecialAll ? activeListId === null : activeListId === list.id;
                const count = isSpecialAll ? properties.length : list.propertyIds.length;
                return (
                  <button
                    key={list.id}
                    type="button"
                    onClick={() => {
                      handlePickVisibleList(list);
                      setIsListPickerOpen(false);
                    }}
                    className={`mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left last:mb-0 ${
                      isActive ? 'bg-[#C4A35A] text-white' : 'text-black hover:bg-[#C4A35A]/10'
                    }`}
                  >
                    <span className="truncate text-sm font-semibold">{list.name}</span>
                    <span className={`text-xs ${isActive ? 'text-white/80' : 'text-black/40'}`}>{count} mục</span>
                  </button>
                );
              })}
            </div>,
            document.body,
          )}
        </div>
      )}

      <div className="flex items-stretch px-2 sm:px-4">
        {visibleLists.length > 0 && (
          <div
            className="relative z-10 hidden w-[5.75rem] shrink-0 flex-col justify-start gap-1.5 pt-6 sm:w-[7.5rem] lg:flex"
            role="tablist"
            aria-label="Danh sách sản phẩm"
          >
            {visibleLists.map((list, index) => {
              const isSpecialAll = list.id === '__all__';
              const isActive = isSpecialAll ? activeListId === null : activeListId === list.id;
              const count = isSpecialAll ? properties.length : list.propertyIds.length;
              return (
                <div
                  key={list.id}
                  className={`relative -mr-2 transition-transform duration-300 ease-out ${
                    isActive ? 'z-20 translate-x-0' : 'z-10 translate-x-2 hover:translate-x-0'
                  }`}
                  style={{ marginTop: index === 0 ? 0 : -4 }}
                >
                  <div
                    className={`flex items-center rounded-l-xl border border-r-0 shadow-sm transition-all duration-300 ${
                      isActive
                        ? 'border-[#C4A35A]/40 bg-[#C4A35A] text-white shadow-[0_8px_18px_rgba(196,163,90,0.28)]'
                        : 'border-black/10 bg-[#F7F1E3] text-[#5C4A2A] hover:bg-[#EFE4C8]'
                    }`}
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handlePickVisibleList(list)}
                      className="flex min-w-0 flex-1 flex-col items-start px-2.5 py-2.5 text-left sm:px-3"
                    >
                      <span className="w-full truncate text-[11px] font-bold leading-tight sm:text-xs">
                        {list.name}
                      </span>
                      <span className={`mt-0.5 text-[10px] ${isActive ? 'text-white/80' : 'text-black/40'}`}>
                        {count} mục
                      </span>
                    </button>
                    {!isSpecialAll && (
                      <div className="relative shrink-0 pr-1">
                        <button
                          type="button"
                          data-list-menu-toggle={list.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenListView(list);
                            const rect = event.currentTarget.getBoundingClientRect();
                            const nextId = activeListMenuId === list.id ? null : list.id;
                            setActiveListMenuId(nextId);
                            if (nextId) {
                              const menuWidth = 176;
                              const left = Math.min(rect.right + 8, window.innerWidth - menuWidth - 8);
                              setListMenuPos({ top: rect.top, left: Math.max(8, left) });
                            } else {
                              setListMenuPos(null);
                            }
                          }}
                          className={`rounded-md p-1 ${
                            isActive ? 'text-white/80 hover:bg-white/15' : 'text-black/30 hover:bg-black/5'
                          }`}
                          title="Tùy chọn danh sách"
                        >
                          <i className="fas fa-ellipsis-v text-[10px]" />
                        </button>
                        {activeListMenuId === list.id && listMenuPos && createPortal(
                          <div
                            ref={listActionMenuRef}
                            data-list-menu="true"
                            style={{ top: listMenuPos.top, left: listMenuPos.left }}
                            className="fixed z-[100000] w-44 rounded-xl border border-black/10 bg-white p-2 text-black shadow-xl"
                          >
                            <button
                              type="button"
                              onClick={() => handleStartEditingList(list)}
                              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm hover:bg-[#C4A35A]/10"
                            >
                              <i className="fas fa-pen mr-2 text-[#C4A35A]"></i>
                              Chỉnh sửa
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveListMenuId(null);
                                setListMenuPos(null);
                                handleDeleteList(list.id);
                              }}
                              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                            >
                              <i className="fas fa-trash mr-2"></i>
                              Xóa
                            </button>
                          </div>,
                          document.body,
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="min-w-0 flex-1 rounded-2xl border border-black/5 bg-[#FFFdf8] p-3 lg:shadow-[inset_8px_0_16px_-12px_rgba(0,0,0,0.18)] sm:p-4">
          <div id="properties-list" className={`grid grid-cols-1 ${compactNative ? 'gap-2' : 'gap-4 md:grid-cols-2'}`}>
            {filteredProperties.length === 0 ? (
              <div className="col-span-full rounded-xl border border-black/5 bg-white p-6 text-center text-black/60">
                {activeList
                  ? `Danh sách "${activeList.name}" chưa có sản phẩm phù hợp.`
                  : searchInputValue.trim()
                    ? 'Không tìm thấy sản phẩm phù hợp.'
                    : 'Chưa có sản phẩm nào để hiển thị.'}
              </div>
            ) : filteredProperties.map((property) => {
              return (
                <PropertyCard 
                  key={property.id} 
                  property={property} 
                  accountType={accountType} 
                  isPublicProfile={isPublicProfile}
                  compactNative={compactNative}
                  onViewDetails={!isPublicProfile ? handleOpenPropertyDetails : undefined}
                  onShowRoomQR={!isPublicProfile && accountType !== 'tenant' ? handleShowRoomQR : undefined}
                  onDelete={!isPublicProfile && accountType !== 'tenant' ? handleDeleteProperty : undefined}
                />
              );
            })}
          </div>
        </div>
      </div>
      <PropertyDetailModal
        isOpen={isDetailModalOpen}
        property={selectedProperty}
        detailLoading={detailLoading}
        onClose={handleClosePropertyDetails}
      />
      <AddContractModal
        isOpen={isContractModalOpen}
        onClose={handleCloseContractModal}
        mode={contractModalMode}
        properties={contractEligibleProperties}
        propertyId={contractRequestPreset?.property_id}
        propertyTitle={contractPresetTitle}
        propertyPrice={contractRequestPreset?.monthly_payment}
        initialData={contractRequestPreset ?? undefined}
        onContractAdded={handleContractAdded}
      />
      <RoomPayQRModal
        isOpen={!!roomQRProperty}
        onClose={() => setRoomQRProperty(null)}
        propertyId={roomQRProperty?.id ?? 0}
        propertyTitle={roomQRProperty?.title ?? ''}
        roomNumber={roomQRProperty?.room_number}
        payRoomCode={roomQRProperty?.pay_room_code}
        payUrl={roomQRProperty?.pay_url}
        onUpdated={handleRoomQRUpdated}
      />
    </div>
  );
};

export default PropertyListings;

